import { CommunityChannelLink, CommunityChannelMode } from './community.models';

/**
 * Contract of `/api/community/admin/*` (Api::CommunityAdminController).
 *
 * Two admin levels: admins (and roles with `manage_roles`) see and change
 * everything; area admins (`manage_group` scoped to a group) only get their
 * groups back and `can_manage_roles: false`.
 */

/** One of the 16 rights-matrix flags (CommunityChannelPermission::FLAGS). */
export type CommunityPermissionFlag =
  | 'view' | 'read' | 'create_thread' | 'comment' | 'react'
  | 'upload_image' | 'upload_file' | 'embed_video' | 'mention' | 'pin'
  | 'manage_links' | 'edit_own' | 'edit_any' | 'delete_own' | 'delete_any' | 'moderate';

export type CommunityPermissionFlags = Record<string, boolean>;

export type CommunityCapability = 'moderate' | 'manage_group' | 'manage_roles' | 'publish_news' | 'view_audit';

/** Role badge visibility scope (CommunityRole::VISIBILITIES). */
export type CommunityRoleVisibility = 'network' | 'group' | 'scope' | 'self' | 'hidden';

/** Group listing (CommunityGroup::VISIBILITIES) — not part of the structure payload. */
export type CommunityGroupVisibility = 'listed' | 'hidden_unless_member';
export const COMMUNITY_GROUP_VISIBILITIES: readonly CommunityGroupVisibility[] = ['listed', 'hidden_unless_member'];

/** Rule types that need a numeric `value`. */
export const COMMUNITY_RULE_TYPES_WITH_NUMBER: readonly string[] = [
  'user_level_at_least',
  'active_directs_at_least',
  'church_rank_at_least',
];
/** Rule type whose `value` is a leadership rank code (from `rank_codes`). */
export const COMMUNITY_RULE_TYPE_RANK = 'wpcp_rank_at_least';

export interface CommunityAutoRule {
  type: string;
  value?: number | string | null;
}

export interface CommunityAdminRole {
  id: number;
  key: string;
  name: string;
  name_i18n: Record<string, string> | null;
  description: string | null;
  badge_label: string | null;
  badge_color: string | null;
  visibility: CommunityRoleVisibility;
  capabilities: string[];
  auto_rule: CommunityAutoRule | null;
  automatic: boolean;
  rule_type: string | null;
  system: boolean;
  position: number;
  active: boolean;
  assignment_count: number;
}

export interface CommunityAdminPermission {
  id: number;
  /** null on a group default row. */
  channel_id: number | null;
  group_id: number;
  role_id: number;
  role_key: string;
  flags: CommunityPermissionFlags;
}

export interface CommunityAdminChannel {
  id: number;
  group_id: number;
  key: string;
  name: string;
  name_i18n: Record<string, string> | null;
  description: string | null;
  position: number;
  mode: CommunityChannelMode;
  read_only: boolean;
  requires_approval: boolean;
  archived: boolean;
  legacy: boolean;
  system: boolean;
  /** Branding site the channel is listed on; null = every site. */
  config_domain_id: string | null;
  allowed_uploads: string[];
  reaction_set: string[];
  thread_count: number;
  /** Per-role override rows of this channel. */
  permissions: CommunityAdminPermission[];
  links: CommunityChannelLink[];
  /** Private Room (irreversible once set). Undefined on older backends -- treat as not private. */
  private?: boolean;
  /** Private Room only: participants may read but not write. */
  participants_read_only?: boolean;
  /** When an existing public channel was turned private; null otherwise. */
  privatized_at?: string | null;
}

export interface CommunityAdminGroup {
  id: number;
  key: string;
  name: string;
  name_i18n: Record<string, string> | null;
  description: string | null;
  icon: string | null;
  position: number;
  visibility: CommunityGroupVisibility;
  system: boolean;
  archived: boolean;
  role_keys: string[];
  /** Group default rows (channel_id null), one per role. */
  default_permissions: CommunityAdminPermission[];
  channels: CommunityAdminChannel[];
}

export interface CommunityAdminStructure {
  can_manage_roles: boolean;
  roles: CommunityAdminRole[];
  groups: CommunityAdminGroup[];
  permission_flags: string[];
  capabilities: string[];
  visibilities: CommunityRoleVisibility[];
  rule_types: { live: string[]; full: string[]; passive: string[] };
  rank_codes: string[];
  channel_modes: CommunityChannelMode[];
  upload_kinds: string[];
  reaction_keys: string[];
  /** Branding sites of the tenant a channel can be bound to. */
  sites?: CommunityAdminSite[];
}

export interface CommunityAdminSite {
  id: string;
  name: string;
}

export interface CommunityAdminUser {
  id: number;
  email: string;
  username: string | null;
  full_name: string | null;
}

export type CommunityAssignmentScopeType = 'CommunityGroup' | 'CommunityChannel';

export interface CommunityAssignment {
  id: number;
  role_id: number;
  role_key: string;
  user: CommunityAdminUser | null;
  scope_type: CommunityAssignmentScopeType | null;
  scope_id: number | null;
  scope_name: string | null;
  source: 'auto' | 'admin' | 'self' | 'api' | string;
  reason: string | null;
  granted_by: CommunityAdminUser | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  revoked_by: CommunityAdminUser | null;
  created_at: string;
  active: boolean;
}

export interface CommunityAssignmentListResponse {
  data: CommunityAssignment[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

// --- Request payloads -----------------------------------------------------------

export interface CommunityRolePayload {
  key?: string;
  name: string;
  name_i18n?: Record<string, string> | null;
  description?: string | null;
  badge_label?: string | null;
  badge_color?: string | null;
  visibility: CommunityRoleVisibility;
  capabilities: string[];
  auto_rule: CommunityAutoRule | null;
  position?: number;
  active?: boolean;
}

export interface CommunityGroupPayload {
  name?: string;
  description?: string | null;
  icon?: string | null;
  visibility?: CommunityGroupVisibility;
  position?: number;
  archived?: boolean;
  role_keys?: string[];
}

export interface CommunityAdminChannelPayload {
  group_id?: number;
  name?: string;
  description?: string | null;
  mode?: CommunityChannelMode;
  read_only?: boolean;
  requires_approval?: boolean;
  allowed_uploads?: string[];
  reaction_set?: string[];
  archived?: boolean;
  position?: number;
  /** '' / null = every site. */
  config_domain_id?: string | null;
  /** `true` turns the channel into a Private Room -- irreversible; `false` is refused (422 private_irreversible). */
  private?: boolean;
  /** Private Room only (422 not_private otherwise). */
  participants_read_only?: boolean;
}

// --- Private Rooms ------------------------------------------------------------

/** Mode a Private Room may have (422 mode_not_allowed_private otherwise). */
export const COMMUNITY_PRIVATE_ROOM_MODES: readonly CommunityChannelMode[] = ['discussion', 'qa'];

export interface CommunityPrivateRoomUser {
  id: number;
  username: string | null;
  full_name: string | null;
  email?: string | null;
}

/** One granted participant of a Private Room (an active role assignment scoped to the channel). */
export interface CommunityPrivateRoomParticipant {
  /** Assignment id -- what `DELETE …/participants/:assignment_id` takes. */
  id: number;
  user: CommunityPrivateRoomUser;
  granted_by: CommunityPrivateRoomUser | null;
  reason: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CommunityPrivateRoomParticipantsResponse {
  participants: CommunityPrivateRoomParticipant[];
  total_count: number;
}

export interface CommunityPrivateRoomParticipantPayload {
  user_id: number;
  reason: string;
  expires_at?: string | null;
}

/** Access of a platform admin who is neither participant nor area admin of the room. */
export interface CommunityPrivateRoomAccessLogEntry {
  id: number;
  user: CommunityPrivateRoomUser | null;
  action: string;
  ip_address: string | null;
  created_at: string;
}

export interface CommunityPrivateRoomAccessLogResponse {
  data: CommunityPrivateRoomAccessLogEntry[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export interface CommunityAssignmentPayload {
  user_id: number;
  role_id: number;
  scope_type?: CommunityAssignmentScopeType | null;
  scope_id?: number | null;
  reason: string;
  expires_at?: string | null;
}

/** Either a group default (`group_id`) or a channel override (`channel_id`). */
export interface CommunityPermissionTarget {
  group_id?: number;
  channel_id?: number;
  role_id: number;
}

// --- Communication rules & contact grants (§8/§9, Phase 3) ------------------

/** How a rule opens messaging from one role to another. */
export type CommunityCommunicationMode = 'any' | 'same_group' | 'sponsor_line';

/**
 * "Members holding `from_role` may write to members holding `to_role`" — a null
 * role on either side means "any member". `depth` only matters for
 * `sponsor_line` (how many levels up/down the sponsor line).
 */
export interface CommunityCommunicationRule {
  id: number;
  from_role_id: number | null;
  from_role_key: string | null;
  to_role_id: number | null;
  to_role_key: string | null;
  mode: CommunityCommunicationMode;
  depth: number;
  description: string | null;
  position: number;
  active: boolean;
}

export interface CommunityCommunicationRulePayload {
  from_role_id?: number | null;
  to_role_id?: number | null;
  mode?: CommunityCommunicationMode;
  depth?: number;
  description?: string | null;
  active?: boolean;
  position?: number;
}

/** A one-directional exception: `user` may write to `contact` regardless of the rules. */
export interface CommunityContactGrant {
  id: number;
  user: CommunityAdminUser | null;
  contact: CommunityAdminUser | null;
  granted_by: CommunityAdminUser | null;
  reason: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  active: boolean;
}

export interface CommunityContactGrantPayload {
  user_id: number;
  contact_id: number;
  reason: string;
  expires_at?: string | null;
  /** Also create the grant in the other direction. */
  both_ways?: boolean;
}

/** `GET /community/admin/communication` (admins / `manage_roles` only). */
export interface CommunityCommunicationResponse {
  rules: CommunityCommunicationRule[];
  modes: CommunityCommunicationMode[];
  grants: CommunityContactGrant[];
}

// --- Statistics (Phase 5) ------------------------------------------------------

export interface CommunityAdminRoleHolder {
  key: string;
  name: string;
  count: number;
}

export interface CommunityAdminStatsGroup {
  id: number;
  key: string;
  name: string;
  active_users_period: number;
}

export interface CommunityAdminStatsChannel {
  channel_id: number;
  channel: string;
  group: string;
  group_id: number;
  threads_total: number;
  threads_period: number;
  posts_total: number;
  posts_period: number;
  active_users_period: number;
  subscribers: number;
}

export interface CommunityAdminStatsWeek {
  week_start: string;
  threads: number;
  posts: number;
  reactions: number;
}

/** `GET /community/admin/stats?days=`. */
export interface CommunityAdminStats {
  period_days: number;
  members_total: number;
  active_users_period: number;
  writers_period: number;
  threads_total: number;
  posts_total: number;
  threads_period: number;
  posts_period: number;
  reactions_total: number;
  news_reads_period: number;
  reports_open: number;
  pending_approval: number;
  role_holders: CommunityAdminRoleHolder[];
  groups: CommunityAdminStatsGroup[];
  channels: CommunityAdminStatsChannel[];
  weekly: CommunityAdminStatsWeek[];
}

// --- Webhooks (Phase 5) ---------------------------------------------------------

export interface CommunityWebhookEndpoint {
  id: number;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
  created_at: string;
}

export interface CommunityWebhookDelivery {
  id: number;
  endpoint_id: number;
  event: string;
  status: string;
  attempts: number;
  response_code: number | null;
  last_error: string | null;
  delivered_at: string | null;
  next_attempt_at: string | null;
  created_at: string;
}

/** `GET /community/admin/webhooks` (`can_manage_roles` only). */
export interface CommunityWebhooksResponse {
  endpoints: CommunityWebhookEndpoint[];
  events: string[];
  deliveries: CommunityWebhookDelivery[];
}

export interface CommunityWebhookPayload {
  name: string;
  url: string;
  events: string[];
  active?: boolean;
  /** `update` only: rotate the signing secret and return the new one once. */
  rotate_secret?: boolean;
}

/** `POST`/`PATCH` responses carry `secret` only right after creation, or after a rotation — shown once, never again. */
export interface CommunityWebhookResponse {
  endpoint: CommunityWebhookEndpoint;
  secret?: string;
}
